import React, { useContext, useState, useEffect, useReducer } from 'react';
import { Redirect } from 'react-router-dom';
import { pickBy, identity } from 'lodash';
import arrayMove from 'array-move';

import Secured from 'Secured';
import App from 'contexts/App';
import UserContext from 'contexts/UserContext';
import Validator, { Parser } from 'utils/Validator';

import Button from 'components/Button';
import { Container, Column } from 'components/Layouts/Flow';
import { Form } from 'components/Form';

import General from 'containers/Administrator/Users/Create_Old/General';
import Address from 'containers/Administrator/Users/Create_Old/Address';
import Teacher from 'containers/Administrator/Users/Create_Old/Teacher';
import Student from 'containers/Administrator/Users/Create_Old/Student';

import { studyPlanGenerator } from 'utils/StudyPlan';

// Initial user's state
const INITIAL = {
	avatar: '',
	name: '',
	surname: '',
	email: '',
	gender: '',
	personalCode: '',
	phoneNumber: '',
	role: '',
	status: '',
	address: {
		country: '',
		city: '',
		street: '',
		zip: ''
	}
};

// User's state reducer
// @state: object | previous user's state
// @action: object | reducer's action
const reducer = (state, { type, payload }) => {
	switch (type) {
	case 'ON_CHANGE':
		return {
			...state,
			[payload.name]: payload.value
		};
	case 'ON_CHANGE_ADDRESS':
		return {
			...state,
			address: {
				...state.address,
				[payload.name]: payload.value
			}
		};
	case 'ON_CHANGE_ROLE':
		if (payload.value === 'teacher') {
			return {
				...state,
				[payload.name]: payload.value,
				courses: [],
				grades: undefined,
				parents: undefined,
				children: undefined
			};
		} else if (payload.value === 'student') {
			return {
				...state,
				[payload.name]: payload.value,
				grades: [],
				parents: [],
				courses: undefined,
				children: undefined
			};
		} else if (payload.value === 'parent') {
			return {
				...state,
				[payload.name]: payload.value,
				children: [],
				grades: undefined,
				courses: undefined,
				parents: undefined,
			};
		} else {
			return {
				...state,
				[payload.name]: payload.value,
				grades: undefined,
				courses: undefined,
				parents: undefined,
				children: undefined
			};
		}
	case 'ON_SELECT_COURSE_TEACHER':
		return {
			...state,
			courses: [
				...state.courses,
				{
					id: payload.id,
					title: payload.title,
					subject: {
						id: payload.subject.id,
						title: payload.subject.title
					},
					teacher: payload.teacher ? {
						id: payload.teacher.id,
						avatar: payload.teacher.avatar,
						fullName: payload.teacher.fullName
					} : null
				}
			]
		};
	case 'ON_REMOVE_COURSE_TEACHER':
		return {
			...state,
			courses: state.courses.filter(course => course.id !== payload.id)
		};
	case 'ON_SELECT_GRADE_STUDENT':
		return {
			...state,
			grades: [
				...state.grades,
				{
					id: payload.id,
					title: payload.title,
					blankId: '',
					startDate: null,
					endDate: null,
					program: {
						id: payload.program.id,
						title: payload.program.title
					},
					blanks: payload.blanks.map(blank => ({
						id: blank.id,
						title: blank.title,
						courses: blank.courses.map(course => {
							// Find exam that is exam, not a test
							// const exam = course.exams.find(e => e.type === 1);

							return {
								id: course.id,
								title: course.title,
								hours: course.hours,
								subject: {
									id: course.subject.id,
									title: course.subject.title
								},
								exam: {
									id: course.exam.id,
									title: course.exam.title
								}
							};
						})
					})),
					courses: []
				}
			]
		};
	case 'ON_REMOVE_GRADE_STUDENT':
		return {
			...state,
			grades: state.grades.filter(grade => grade.id !== payload.id)
		};
	case 'ON_CHANGE_GRADE_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.id) {
					return {
						...grade,
						[payload.name]: payload.value
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_SELECT_BLANK_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					return {
						...grade,
						[payload.name]: payload.value,
						courses: payload.courses.map(course => ({
							id: course.id,
							title: course.title,
							hours: course.hours,
							startDate: null,
							subject: {
								id: course.subject.id,
								title: course.subject.title
							},
							exam: {
								id: course.exam.id,
								title: course.exam.title,
								startDate: null,
								endDate: null
							}
						}))
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_SELECT_COURSE_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					// Find exam that is exam, not a test
					const exam = payload.course.exams.find(e => e.type === 1);

					return {
						...grade,
						courses: [
							...grade.courses,
							{
								id: payload.course.id,
								title: payload.course.title,
								startDate: null,
								subject: {
									id: payload.course.subject.id,
									title: payload.course.subject.title
								},
								exam: {
									id: exam.id,
									title: exam.title,
									startDate: null,
									endDate: null
								}
							}
						]
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_REMOVE_COURSE_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					return {
						...grade,
						courses: grade.courses.filter(course => course.id !== payload.id)
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_CHANGE_COURSE_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					return {
						...grade,
						courses: grade.courses.map(course => {
							if (course.id === payload.id) {
								return {
									...course,
									[payload.name]: payload.value
								};
							} else {
								return course;
							}
						})
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_CHANGE_EXAM_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					return {
						...grade,
						courses: grade.courses.map(course => {
							if (course.id === payload.id) {
								return {
									...course,
									exam: {
										...course.exam,
										[payload.name]: payload.value
									}
								};
							} else {
								return course;
							}
						})
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_SORT_COURSE_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.gradeId) {
					return {
						...grade,
						courses: arrayMove(grade.courses, payload.old, payload.new)
					};
				} else {
					return grade;
				}
			})
		};
	case 'ON_GENERATE_PLAN_STUDENT':
		return {
			...state,
			grades: state.grades.map(grade => {
				if (grade.id === payload.id) {
					return {
						...grade,
						courses: studyPlanGenerator(grade)
					};
				} else {
					return grade;
				}
			})
		};
	default:
		console.trace('Undefined action: ', type);
		return state;
	}
};

// Prepare user's data for validation
// @state: object | user's state
const prepare = state => {
	switch (state.role) {
	case 'teacher':
		return {
			...pickBy(state, identity),
			address: pickBy(state.address, identity),
			courses: state.courses.map(course => pickBy(course, identity))
		};
	case 'student':
		return {
			...pickBy(state, identity),
			address: pickBy(state.address, identity),
			grades: state.grades.map(grade => ({
				...pickBy(grade, identity),
				courses: grade.courses.map(course => ({
					...pickBy(course, identity),
					exam: pickBy(course.exam, identity)
				}))
			}))
		};
	case 'parent':
	default:
		return {
			...pickBy(state, identity),
			address: pickBy(state.address, identity)
		};
	}
};

const Create = Secured(({ request }) => {
	// Use app's context
	const { setTitle, notify } = useContext(App);

	// Set tab on load
	useEffect(() => setTitle('Jauns lietotājs'), [setTitle]);

	// Handle user's state with reducer
	const [user, handleUser] = useReducer(reducer, INITIAL);

	// Cache user's errors
	const [errors, setErrors] = useState({});

	// Cache request progress
	const [progress, setProgress] = useState(false);

	// Cache redirectison state
	const [redirect, setRedirect] = useState(false);

	// Handles form's onSubmit event
	// @event: object | onSubmit event
	const onSubmit = event => {
		event.preventDefault();

		// Do not submit if request in progress
		if (progress) return;

		// Prepare data for validation and submittion
		const data = prepare(user);

		// Validate user's data
		Validator.validate('/user', data);

		// TODO: Remove after testing
		console.table(Validator.errors);

		// Update error's if there were any
		if (Validator.errors) return setErrors(Parser(Validator.errors));

		// Create formdata's instance
		const formdata = new FormData();

		// Append all plain text data as stringified object
		formdata.append('data', JSON.stringify(data));

		// Append avatar
		if (data.avatar) formdata.append('avatar', data.avatar);

		// Set request progress state
		setProgress(true);

		// Try to create new user
		request('post', 'users', null, formdata, res => {
			// Reset request progress state
			setProgress(false);

			if (res.action) {
				// Redirect to all users
				setRedirect(true);

				// Create notification
				notify('success', res.action);

			} else if (res.error === 'validationError') {
				// Validate user's data
				Validator.validate('/user', user);

				// Update error's if there were any
				if (Validator.errors) setErrors(Parser(Validator.errors));

				// Create notification
				notify('invalid', res.error);

			} else if (res.error === 'validationProgramExists') {
				// Update user's errors
				setErrors({
					title: 'error - should be translated'
				});

				// Create notification
				notify('invalid', res.error);

			} else {
				// Create notification
				notify('warning', res.error);
			}
		});
	};

	// Redirect to all users if request was successful
	if (redirect) return <Redirect to="/users" />;

	return (
		<>
			{/* Creation page's tabs */}
			{/* <Page withBack base="/users" /> */}

			{/* Creation form */}
			<Form progress={progress} onSubmit={onSubmit} fixedWidth withBackground withPaddingAll>
				<Container>
					<UserContext.Provider value={{ user, errors, handleUser }}>
						{/* User's general information */}
						<General errors={errors}	/>

						{/* User's address information */}
						<Address errors={errors?.address || {}} />

						{/* Teacher's information */}
						{user.role === 'teacher' && <Teacher />}

						{/* Student's information */}
						{user.role === 'student' && <Student />}
					</UserContext.Provider>

					<Column size="12">
						<Button submit icon="check" text="Izveidot" />
					</Column>
				</Container>
			</Form>
		</>
	);
});

export default Create;
